import React, { useState, useEffect, useContext } from "react";
import {
  CreditCard,
  Download,
  Calendar,
  DollarSign,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
} from "lucide-react";
import axios from "axios";
import config from "../config/environment";
import { handleApiError, logError } from "../utils/errorHandler";
import { AuthContext } from "../context/AuthContext";

const BillingHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const { userId } = useContext(AuthContext);

  useEffect(() => {
    fetchBillingHistory();
  }, [userId]);

  const fetchBillingHistory = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${config.api.baseUrl}/api/subscriptions/billing-history`,
        {
          params: { userId },
          timeout: config.api.timeout,
        }
      );

      setTransactions(response.data.transactions || []);
    } catch (err) {
      const handledError = handleApiError(err);
      setError(handledError.message);
      logError(handledError, { context: "billing_history", userId });
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (transactionId) => {
    setDownloading((prev) => ({ ...prev, [transactionId]: true }));

    try {
      const response = await axios.get(
        `${config.api.baseUrl}/api/subscriptions/invoice/${transactionId}`,
        {
          params: { userId },
          responseType: "blob",
          timeout: config.api.timeout,
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${transactionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const handledError = handleApiError(err);
      setError(`Failed to download invoice: ${handledError.message}`);
      logError(handledError, {
        context: "invoice_download",
        transactionId,
        userId,
      });
    } finally {
      setDownloading((prev) => ({ ...prev, [transactionId]: false }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "paid":
        return <CheckCircle className="text-green-500" size={16} />;
      default:
        return <AlertCircle className="text-gray-500" size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "paid":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatAmount = (amount, currency = "USD") => {
    if (
      typeof amount === "string" &&
      (amount.startsWith("$") || amount.includes(currency))
    ) {
      // Handle pre-formatted amounts like "$19.00" or "19.00 USD"
      return amount;
    }

    // Handle numeric amounts (assume they're already in dollars, not cents)
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading billing history...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CreditCard className="text-gray-400" size={20} />
            <h2 className="text-lg font-medium text-gray-900">
              Billing History
            </h2>
          </div>
          <div className="flex items-center space-x-3">
            {/* Refresh Button */}
            <button
              onClick={fetchBillingHistory}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
              disabled={loading}
            >
              <RefreshCw className={loading ? "animate-spin" : ""} size={16} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="text-red-500 mt-0.5" size={16} />
            <div className="flex-1">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-xs text-red-600 hover:text-red-800 mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="divide-y divide-gray-200">
        {transactions.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No billing history
            </h3>
            <p className="text-gray-600">
              You don't have any completed payments yet.
            </p>
          </div>
        ) : (
          transactions.map((transaction) => (
            <div key={transaction.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    {getStatusIcon(transaction.status)}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {transaction.description || "SQL Guroo Subscription"}
                        {transaction.plan_name && (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            {transaction.plan_name}
                          </span>
                        )}
                        {transaction.items_count > 1 && (
                          <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {transaction.items_count} items
                          </span>
                        )}
                        {transaction.source ===
                          "paddle_transactions_joined" && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                            Verified
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-gray-500">
                        Transaction ID: {transaction.id}
                        {transaction.invoice_number && (
                          <span className="ml-2">
                            • Invoice: {transaction.invoice_number}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>
                        {formatDate(
                          transaction.transaction_date ||
                            transaction.created_at ||
                            transaction.date
                        )}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <DollarSign size={14} />
                      <span>
                        {formatAmount(transaction.amount, transaction.currency)}
                        {transaction.tax && transaction.tax !== "$0.00" && (
                          <span className="ml-1 text-xs text-gray-500">
                            (Tax: {transaction.tax})
                          </span>
                        )}
                        {transaction.discount &&
                          transaction.discount !== "$0.00" && (
                            <span className="ml-1 text-xs text-green-600">
                              (Discount: {transaction.discount})
                            </span>
                          )}
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        transaction.status
                      )}`}
                    >
                      {transaction.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {/* Download Invoice Button */}
                  {(transaction.status?.toLowerCase() === "completed" ||
                    transaction.status?.toLowerCase() === "paid") && (
                    <button
                      onClick={() => downloadInvoice(transaction.id)}
                      disabled={downloading[transaction.id]}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1 disabled:opacity-50"
                    >
                      <Download
                        className={
                          downloading[transaction.id] ? "animate-spin" : ""
                        }
                        size={14}
                      />
                      <span>
                        {downloading[transaction.id]
                          ? "Downloading..."
                          : "Invoice"}
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {/* Additional Transaction Details */}
              {(transaction.origin ||
                transaction.subscription_id ||
                transaction.subtotal ||
                transaction.user_id) && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500 space-y-1">
                    {transaction.origin && (
                      <div>Origin: {transaction.origin}</div>
                    )}
                    {transaction.subscription_id && (
                      <div>Subscription: {transaction.subscription_id}</div>
                    )}
                    {transaction.subtotal &&
                      transaction.subtotal !== transaction.amount && (
                        <div>
                          Subtotal: {transaction.subtotal}
                          {transaction.tax &&
                            transaction.tax !== "$0.00" &&
                            ` • Tax: ${transaction.tax}`}
                          {transaction.discount &&
                            transaction.discount !== "$0.00" &&
                            ` • Discount: ${transaction.discount}`}
                        </div>
                      )}
                    {transaction.user_id && (
                      <div>User ID: {transaction.user_id}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {transactions.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Showing {transactions.length} transactions</span>
            <span>All amounts are in USD unless otherwise noted</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingHistory;
